import * as React from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { auth } from "../config/firebase";
import { AppHeader } from "../components/AppHeader.tsx";
import Library from "../components/Library.tsx";
import ImageViewerModal from "../components/ImageViewerModal.tsx";
import MarketplaceExportModal from "../components/MarketplaceExportModal.tsx";
import { ReferralHubButton } from "../components/ReferralHub.tsx";
import { DashboardNavPills } from "../components/DashboardNavPills.tsx";
import {
  loadAllUserLibraryContent,
  deleteGeneratedImage,
  renameGeneratedContent,
  GLOBAL_LIBRARY_PAGE_SIZE,
  GLOBAL_LIBRARY_QUERY_KEY,
  GlobalLibraryScene,
  invalidateGlobalLibrary,
} from "../services/studioPersistenceService.ts";
import { downloadAsset } from "../utils/imageUtils.ts";
import { fetchActiveAiBadge } from "../services/aiBadgeService.ts";
import { useUserSettings } from "../hooks/useUserSettings.ts";
import {
  trackImageDownloaded,
  trackImageRenamed,
} from "../services/analyticsService.ts";
import { reportError } from "../utils/errorReporting.ts";
import { resolveErrorMessage } from "../utils/errorResolver.ts";
import { GeneratedContent, StudioProduct, AppImageFile } from "../types.ts";

interface ActiveViewer {
  productId: string;
  contentId: string;
}

type GlobalLibraryPage = {
  products: StudioProduct[];
  scenes: GlobalLibraryScene[];
  hasMore: boolean;
};

type GlobalLibraryInfiniteData = {
  pages: GlobalLibraryPage[];
  pageParams: number[];
};

const GlobalLibrary: React.FC = () => {
  const { t } = useTranslation();
  const { aiWatermarkEnabled, aiWatermarkStyle } = useUserSettings();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = auth.currentUser?.uid;

  const queryKey = React.useMemo(
    () => [...GLOBAL_LIBRARY_QUERY_KEY, userId] as const,
    [userId]
  );

  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      loadAllUserLibraryContent({
        limit: GLOBAL_LIBRARY_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore ? allPages.length * GLOBAL_LIBRARY_PAGE_SIZE : undefined,
    enabled: !!userId,
    retry: 1,
    // Cache library state across navigations within a session — the user
    // bouncing between /library and a project shouldn't refetch every page.
    staleTime: 5 * 60 * 1000,
  });

  React.useEffect(() => {
    if (error)
      reportError(error, { context: "GlobalLibrary.useInfiniteQuery" });
  }, [error]);

  const products: StudioProduct[] = React.useMemo(
    () => data?.pages.flatMap((p) => p.products) ?? [],
    [data]
  );
  const scenes: GlobalLibraryScene[] = React.useMemo(
    () => data?.pages.flatMap((p) => p.scenes) ?? [],
    [data]
  );

  // Multi-product scenes don't fit the StudioProduct shape natively, so adapt
  // them to the shape Library.tsx renders. Mirrors the per-project library at
  // src/routes/projects.$projectId.library.tsx.
  const scenesAsProducts: StudioProduct[] = React.useMemo(
    () =>
      scenes.map((scene) => {
        const placeholder: AppImageFile = {
          id: "",
          base64: "",
          mimeType: "image/png",
          name: "",
        };
        return {
          id: scene.id,
          image: placeholder,
          originalImage: { ...placeholder, name: scene.name },
          info: "multiproduct",
          itemCount: scene.products.length,
          isSelected: false,
          isProcessing: false,
          generatedContent: scene.generatedContent,
          isGenerating: scene.isGenerating,
          error: scene.error,
          linkedProductIds: scene.products.map((p) => p.productId),
          projectId: scene.projectId,
          projectName: scene.projectName,
        } satisfies StudioProduct;
      }),
    [scenes]
  );

  const allProducts: StudioProduct[] = React.useMemo(
    () => [...products, ...scenesAsProducts],
    [products, scenesAsProducts]
  );

  const [activeViewer, setActiveViewer] = React.useState<ActiveViewer | null>(
    null
  );

  const [exportTarget, setExportTarget] = React.useState<{
    content: GeneratedContent;
    projectId: string;
    productId: string;
  } | null>(null);

  const handleExportMarketplace = (productId: string, contentId: string) => {
    const product = allProducts.find((p) => p.id === productId);
    const content = product?.generatedContent.find((c) => c.id === contentId);
    if (product && content && product.projectId) {
      setExportTarget({
        content,
        projectId: product.projectId,
        productId: product.id,
      });
    }
  };

  const findProduct = React.useCallback(
    (productId: string): StudioProduct | undefined =>
      allProducts.find((p) => p.id === productId),
    [allProducts]
  );

  const findContent = React.useCallback(
    (
      productId: string,
      contentId: string
    ): { product: StudioProduct; content: GeneratedContent } | null => {
      const product = findProduct(productId);
      if (!product) return null;
      const content = product.generatedContent.find((c) => c.id === contentId);
      if (!content) return null;
      return { product, content };
    },
    [findProduct]
  );

  const handleViewImage = (productId: string, contentId: string) => {
    setActiveViewer({ productId, contentId });
  };

  const handleDownloadImage = async (
    imageUrl: string,
    filename: string,
    format: "png" | "jpeg" | "mp4" = "png"
  ) => {
    // Look up which product/content this URL belongs to so the download
    // flow can resolve the full-res asset (vs the _1024 thumbnail it was
    // handed) and stamp the analytics event with the right project/product.
    // `storagePath` is matched too because the global library renders
    // images lazily and passes the storage path until the thumbnail URL
    // resolves on scroll-into-view.
    const matchesContent = (c: GeneratedContent) =>
      c.image === imageUrl ||
      c.thumbnailUrl === imageUrl ||
      c.video === imageUrl ||
      c.fullUrl === imageUrl ||
      c.storagePath === imageUrl;
    const matchingProduct = allProducts.find((p) =>
      p.generatedContent.some(matchesContent)
    );
    const matchingContent =
      matchingProduct?.generatedContent.find(matchesContent);
    // The global library only lists AI-generated results, so the opt-in
    // "AI-generated" pill applies to every non-video download here.
    const stampAiPill =
      !matchingContent?.video && format !== "mp4" && aiWatermarkEnabled;
    // Discriminated result: an unreachable badge library must not silently
    // downgrade a selected marketplace label to the pill — warn, then
    // deliver with the settings pill (analytics stay truthful below).
    const badgeResult = stampAiPill ? await fetchActiveAiBadge() : null;
    if (badgeResult?.status === "unavailable") {
      toast.error(t("errors.watermark/badge_library_unavailable"));
    }
    const activeBadge =
      badgeResult?.status === "ready" ? badgeResult.badge : null;
    const { stamped, deliveryPath } = await downloadAsset({
      url: imageUrl,
      filename,
      format,
      storagePath: matchingContent?.storagePath,
      isVideo: !!matchingContent?.video,
      watermarkText: stampAiPill ? t("studio.aiWatermarkLabel") : null,
      watermarkStyle: aiWatermarkStyle,
      watermarkBadge: activeBadge,
    });
    // Tracked AFTER the download so `aiWatermark` reflects whether the badge
    // actually ended up on the file, not just the request intent.
    trackImageDownloaded({
      format,
      storagePath: matchingContent?.storagePath,
      imageId: matchingContent?.id,
      source: "generated",
      projectId: matchingProduct?.projectId,
      productId: matchingProduct?.id,
      aiWatermark: stamped,
      aiBadgeKind: stamped ? (activeBadge?.kind ?? "styled") : undefined,
      deliveryPath,
    });
  };

  const handleDeleteImage = async (productId: string, contentId: string) => {
    const target = findContent(productId, contentId);
    if (!target) return;

    // Optimistic update: drop the row from every cached page immediately so
    // the grid responds without waiting for a refetch.
    queryClient.setQueryData<GlobalLibraryInfiniteData>(queryKey, (prev) => {
      if (!prev) return prev;
      const stripContent = <T extends { generatedContent: GeneratedContent[] }>(
        items: T[]
      ): T[] =>
        items.map((item) =>
          (item as unknown as StudioProduct).id === productId
            ? {
                ...item,
                generatedContent: item.generatedContent.filter(
                  (c) => c.id !== contentId
                ),
              }
            : item
        );
      return {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          products: stripContent(page.products),
          scenes: stripContent(page.scenes),
        })),
      };
    });

    if (activeViewer?.contentId === contentId) {
      setActiveViewer(null);
    }

    try {
      await deleteGeneratedImage(contentId, target.content.storagePath ?? null);
    } catch (e) {
      reportError(e, { context: "GlobalLibrary.handleDeleteImage" });
      toast.error(resolveErrorMessage(e, t));
      // Roll back the optimistic update.
      queryClient.invalidateQueries({ queryKey });
    }
  };

  /**
   * Renames a gallery image and patches it in every cached page.
   *
   * Mirrors `handleDeleteImage`: the grid and the open viewer both render from
   * the query cache, so the optimistic write is what makes the new name appear
   * without a refetch. `filename` moves with `title` because the viewer's
   * Details tab reads `filename || title` while its header reads `title`
   * first.
   *
   * On failure the previous title is written back EXPLICITLY rather than left
   * to the invalidation's refetch. Relying on the refetch leaves the optimistic
   * title in the cache whenever it is slow, paused or itself fails — and a
   * stuck optimistic title is worse than a stale one: the viewer's retry of the
   * same name would then find `content.title` already equal to the draft, so
   * `resolveRenamedTitle` returns null, the editor closes, and the rename is
   * silently never written while the UI keeps showing it. The error is
   * re-thrown unreported; the viewer's catch is what surfaces it to the user
   * and files it to Sentry, so reporting here too would double-count one
   * failure.
   */
  const handleRenameImage = async (
    productId: string,
    contentId: string,
    title: string
  ) => {
    const patchCachedTitle = (
      next: Pick<GeneratedContent, "title" | "filename">
    ) =>
      queryClient.setQueryData<GlobalLibraryInfiniteData>(queryKey, (prev) => {
        if (!prev) return prev;
        const renameContent = <
          T extends { generatedContent: GeneratedContent[] },
        >(
          items: T[]
        ): T[] =>
          items.map((item) =>
            (item as unknown as StudioProduct).id === productId
              ? {
                  ...item,
                  generatedContent: item.generatedContent.map((c) =>
                    c.id === contentId ? { ...c, ...next } : c
                  ),
                }
              : item
          );
        return {
          ...prev,
          pages: prev.pages.map((page) => ({
            ...page,
            products: renameContent(page.products),
            scenes: renameContent(page.scenes),
          })),
        };
      });

    const before = findContent(productId, contentId)?.content;
    const previous = {
      title: before?.title,
      filename: before?.filename,
    };

    patchCachedTitle({ title, filename: title });

    try {
      await renameGeneratedContent(contentId, title);
      trackImageRenamed({
        imageId: contentId,
        surface: "global_library",
        productId,
      });
    } catch (e) {
      patchCachedTitle(previous);
      queryClient.invalidateQueries({ queryKey });
      throw e;
    }
  };

  const viewerData = activeViewer
    ? findContent(activeViewer.productId, activeViewer.contentId)
    : null;

  return (
    <div className="h-full flex flex-col bg-gray-50">
      <AppHeader
        center={<DashboardNavPills active="library" />}
        rightExtras={<ReferralHubButton />}
      />
      <div className="flex-grow overflow-hidden flex flex-col">
        {error ? (
          <div className="flex-grow flex items-center justify-center p-8">
            <div className="max-w-md text-center">
              <p className="text-base font-semibold text-gray-900">
                {resolveErrorMessage(error, t)}
              </p>
              <button
                type="button"
                onClick={() => refetch()}
                className="mt-4 inline-flex items-center px-4 py-2 rounded-full bg-black text-white text-sm font-semibold hover:bg-gray-800 transition"
              >
                {t("common.retry")}
              </button>
            </div>
          </div>
        ) : (
          <>
            <Library
              products={allProducts}
              onViewImage={handleViewImage}
              onDownloadImage={handleDownloadImage}
              onDeleteImage={handleDeleteImage}
              onExportMarketplace={handleExportMarketplace}
              isLoading={isLoading}
              onEndReached={() => {
                if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
              }}
              isFetchingMore={isFetchingNextPage}
              hasMore={!!hasNextPage}
            />
          </>
        )}
      </div>

      {viewerData &&
        (() => {
          const siblings = viewerData.product.generatedContent;
          const currentIdx = siblings.findIndex(
            (c) => c.id === viewerData.content.id
          );
          const focusSibling = (idx: number) => {
            const target = siblings[idx];
            if (!target) return;
            setActiveViewer({
              productId: viewerData.product.id,
              contentId: target.id,
            });
          };
          const projectIdForLink = viewerData.product.projectId;
          return (
            <ImageViewerModal
              content={viewerData.content}
              onClose={() => setActiveViewer(null)}
              onDownload={handleDownloadImage}
              onDelete={(contentId) =>
                handleDeleteImage(viewerData.product.id, contentId)
              }
              onRename={(contentId, title) =>
                handleRenameImage(viewerData.product.id, contentId, title)
              }
              allImages={siblings}
              onSelectImage={(selection) => {
                if (selection === -1) return;
                setActiveViewer({
                  productId: viewerData.product.id,
                  contentId: selection,
                });
              }}
              currentIndex={currentIdx}
              totalImages={siblings.length}
              onPrevious={() => focusSibling(currentIdx - 1)}
              onNext={() => focusSibling(currentIdx + 1)}
              projectName={viewerData.product.projectName}
              projectId={projectIdForLink}
              productId={viewerData.product.id}
              onExportSaved={() => invalidateGlobalLibrary()}
              onOpenInProject={
                projectIdForLink
                  ? () =>
                      // Land on the Studio (Image) tab, not the Library tab:
                      // the ImageViewerModal conceptually belongs to the Studio,
                      // so closing it should leave the user in the Studio rather
                      // than confusingly on the project's Library tab.
                      navigate({
                        to: "/projects/$projectId",
                        params: { projectId: projectIdForLink },
                        search: { image: viewerData.content.id },
                      })
                  : undefined
              }
            />
          );
        })()}

      {exportTarget && (
        <MarketplaceExportModal
          content={exportTarget.content}
          projectId={exportTarget.projectId}
          productId={exportTarget.productId}
          onExportSaved={() => invalidateGlobalLibrary()}
          onClose={() => setExportTarget(null)}
        />
      )}
    </div>
  );
};

export const Route = createFileRoute("/library")({
  component: GlobalLibrary,
});
